import { useCallback, useEffect, useMemo, useState } from "react";
import ToolIcon from "./ToolIcon";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
const API_UNCONFIGURED = API_BASE.includes("your-api-gateway");

function parseApiErrorText(text) {
  if (!text) return "Unknown API error";

  try {
    const parsed = JSON.parse(text);
    return parsed?.error || parsed?.message || text;
  } catch {
    return text;
  }
}

async function apiRequest(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const hasBody = options.body != null;
  const defaultHeaders = hasBody || method !== "GET"
    ? { "Content-Type": "application/json" }
    : {};

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: { ...defaultHeaders, ...(options.headers || {}) },
      ...options,
    });

    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    if (!response.ok) {
      throw new Error(parseApiErrorText(text));
    }
    return data;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error("API returned invalid JSON.");
    }
    if (error instanceof TypeError) {
      throw new Error("Cannot reach AWS API. Verify API Gateway URL, CORS, and deployment.");
    }
    throw error;
  }
}

function parseDate(value) {
  if (!value) return Number.MAX_SAFE_INTEGER;

  const direct = Date.parse(value);
  if (!Number.isNaN(direct)) return direct;

  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM))?$/i);
  if (!match) return Number.MAX_SAFE_INTEGER;

  const month = Number(match[1]) - 1;
  const day = Number(match[2]);
  const year = Number(match[3]);
  let hour = Number(match[4] || 0);
  const minute = Number(match[5] || 0);
  const second = Number(match[6] || 0);
  const meridiem = (match[7] || "").toUpperCase();

  if (meridiem === "PM" && hour < 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;

  return new Date(year, month, day, hour, minute, second).getTime();
}

function formatValue(value, fallback = "-") {
  if (value == null || value === "") return fallback;
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return String(value);
}

function formatDateLabel(value) {
  if (!value) return "-";
  const parsed = parseDate(value);
  if (parsed === Number.MAX_SAFE_INTEGER) return String(value);
  return new Date(parsed).toLocaleString();
}

function isMissingSnapshotsMessage(message) {
  return String(message || "").includes("start and end snapshots are required");
}

function downloadFile(filename, contents, contentType) {
  const blob = new Blob([contents], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildSummaryCsv(report) {
  const summary = report?.summary || {};
  const unplanned = report?.unplanned?.counts || {};
  const rows = [
    ["metric", "value"],
    ["startingStories", summary.startingStories ?? 0],
    ["startingPoints", summary.startingPoints ?? 0],
    ["addedStories", summary.addedStories ?? 0],
    ["addedPoints", summary.addedPoints ?? 0],
    ["canceledOrMovedOutStories", summary.canceledOrMovedOutStories ?? 0],
    ["canceledOrMovedOutPoints", summary.canceledOrMovedOutPoints ?? 0],
    ["carryoverStories", summary.carryoverStories ?? 0],
    ["carryoverPoints", summary.carryoverPoints ?? 0],
    ["endingStories", summary.endingStories ?? 0],
    ["endingPoints", summary.endingPoints ?? 0],
    ["storyDifference", summary.storyDifference ?? 0],
    ["pointDifference", summary.pointDifference ?? 0],
    ["totalUnplanned", unplanned.totalUnplanned ?? 0],
    ["incidents", unplanned.incidents ?? 0],
    ["hrc", unplanned.hrc ?? 0],
    ["tasks", unplanned.tasks ?? 0],
    ["kfts", unplanned.kfts ?? 0],
    ["incidentsWrongType", unplanned.incidentsWrongType ?? 0],
    ["incOrTaskMissingAffectedCi", unplanned.incOrTaskMissingAffectedCi ?? 0],
  ];

  return rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
}

function buildPeopleSummary(report) {
  const classifications = report?.classifications || {};
  const buckets = [
    ...(classifications.added || []).map((row) => ({ ...row, bucket: "added" })),
    ...(classifications.carryover || []).map((row) => ({ ...row, bucket: "carryover" })),
    ...(classifications.stayedInSprint || []).map((row) => ({ ...row, bucket: "stayedInSprint" })),
    ...(classifications.movedToBacklog || []).map((row) => ({ ...row, bucket: "movedToBacklog" })),
    ...(classifications.movedToOtherSprint || []).map((row) => ({ ...row, bucket: "movedToOtherSprint" })),
    ...(classifications.removedUnknown || []).map((row) => ({ ...row, bucket: "removedUnknown" })),
  ];

  const byPerson = new Map();
  for (const item of buckets) {
    // Handle both string and potential object responses from backend
    let assignee = item.assigned_to || "Unassigned";
    if (typeof assignee === "object" && assignee !== null) {
      assignee = assignee.display_value || assignee.value || "Unassigned";
    }
    assignee = String(assignee).trim() || "Unassigned";
    
    if (!byPerson.has(assignee)) {
      byPerson.set(assignee, {
        assignee,
        totalStories: 0,
        totalPoints: 0,
        added: 0,
        carryover: 0,
        stayedInSprint: 0,
        movedOut: 0,
      });
    }

    const row = byPerson.get(assignee);
    row.totalStories += 1;
    row.totalPoints += Number(item.story_points || 0);
    if (item.bucket === "added") row.added += 1;
    if (item.bucket === "carryover") row.carryover += 1;
    if (item.bucket === "stayedInSprint") row.stayedInSprint += 1;
    if (item.bucket === "movedToBacklog" || item.bucket === "movedToOtherSprint" || item.bucket === "removedUnknown") {
      row.movedOut += 1;
    }
  }

  return [...byPerson.values()]
    .sort((a, b) => b.totalPoints - a.totalPoints || b.totalStories - a.totalStories || a.assignee.localeCompare(b.assignee))
    .map((row) => ({ ...row, totalPoints: Number(row.totalPoints.toFixed(2)) }));
}

function SnapshotRow({ snapshot }) {
  return (
    <div className="metrics-snapshot-row">
      <span className={`metrics-snapshot-pill metrics-snapshot-pill-${snapshot.snapshotType || "unknown"}`}>
        {formatValue(snapshot.snapshotType, "unknown")}
      </span>
      <span className="metrics-snapshot-key" title={snapshot.key}>{snapshot.key}</span>
    </div>
  );
}

function ClassificationTable({ title, rows, fullWidth = false, scrollable = false }) {
  const cardClass = fullWidth ? "metrics-panel-card" : "metrics-section-card";
  const headerClass = fullWidth ? "metrics-panel-header" : "metrics-section-header";
  const tableWrapClass = scrollable
    ? "metrics-table-wrap metrics-stayed-in-sprint-scroll"
    : "metrics-table-wrap";

  if (!rows || rows.length === 0) {
    return (
      <section className={cardClass}>
        <div className={headerClass}>
          {fullWidth ? <h2>{title}</h2> : <h3>{title}</h3>}
          <span className="metrics-count-badge">0</span>
        </div>
        <div className="metrics-empty-state">No items in this category.</div>
      </section>
    );
  }

  return (
    <section className={cardClass}>
      <div className={headerClass}>
        {fullWidth ? <h2>{title}</h2> : <h3>{title}</h3>}
        <span className="metrics-count-badge">{rows.length}</span>
      </div>
      <div className={tableWrapClass}>
        <table className="metrics-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Title</th>
              <th>Assigned To</th>
              <th>State</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.sys_id || `${row.number}-${row.title}`}>
                <td>{formatValue(row.number)}</td>
                <td title={formatValue(row.title)}>{formatValue(row.title)}</td>
                <td>{formatValue(row.assigned_to)}</td>
                <td>{formatValue(row.state)}</td>
                <td>{formatValue(row.story_points, "0")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function SprintMetrics() {
  const [sprints, setSprints] = useState([]);
  const [selectedSprintId, setSelectedSprintId] = useState("");
  const [snapshots, setSnapshots] = useState([]);
  const [report, setReport] = useState(null);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generatingSummaries, setGeneratingSummaries] = useState(false);
  const [captureType, setCaptureType] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [exporting, setExporting] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");

  const selectedSprint = useMemo(
    () => sprints.find((sprint) => sprint.sys_id === selectedSprintId) || null,
    [sprints, selectedSprintId]
  );

  const loadSprints = useCallback(async () => {
    if (API_UNCONFIGURED) {
      setError("REACT_APP_API_BASE is not configured. Set it to your API Gateway stage URL.");
      setSprints([]);
      return;
    }

    setLoadingSprints(true);
    try {
      const data = await apiRequest("/sn-sprints");
      const sorted = [...(data.sprints || [])].sort((left, right) => parseDate(left.start_date) - parseDate(right.start_date));
      setSprints(sorted);
      if (!selectedSprintId && sorted[0]?.sys_id) {
        setSelectedSprintId(sorted[0].sys_id);
      }
      setError("");
    } catch (requestError) {
      setError(requestError.message || "Unable to load ServiceNow sprints.");
      setSprints([]);
    } finally {
      setLoadingSprints(false);
    }
  }, [selectedSprintId]);

  const loadSnapshots = useCallback(async (sprintId) => {
    if (!sprintId) {
      setSnapshots([]);
      return;
    }

    setLoadingSnapshots(true);
    try {
      const data = await apiRequest(`/sn-metrics/snapshots?sprintId=${encodeURIComponent(sprintId)}`);
      setSnapshots(data.snapshots || []);
      setError("");
    } catch (requestError) {
      setSnapshots([]);
      setError(requestError.message || "Unable to load snapshots.");
    } finally {
      setLoadingSnapshots(false);
    }
  }, []);

  const loadReport = useCallback(async (sprintId, { archiveDetails = false, endSnapshotKey = "" } = {}) => {
    if (!sprintId) {
      setReport(null);
      return;
    }

    setLoadingReport(true);
    setGeneratingSummaries(archiveDetails);
    try {
      const params = new URLSearchParams({ sprintId, includeUnplanned: "true" });
      if (archiveDetails) {
        params.set("archiveDetails", "true");
        if (endSnapshotKey) params.set("endKey", endSnapshotKey);
      }
      const data = await apiRequest(`/sn-metrics/report?${params.toString()}`);
      setReport(data);
      setError("");
    } catch (requestError) {
      if (!archiveDetails) setReport(null);
      if (isMissingSnapshotsMessage(requestError.message)) {
        setError("");
      } else {
        setError(requestError.message || "Unable to load sprint report.");
      }
    } finally {
      setLoadingReport(false);
      setGeneratingSummaries(false);
    }
  }, []);

  useEffect(() => {
    loadSprints();
  }, [loadSprints]);

  useEffect(() => {
    if (!selectedSprintId) return;
    loadSnapshots(selectedSprintId);
    loadReport(selectedSprintId);
  }, [selectedSprintId, loadReport, loadSnapshots]);

  const handleCaptureSnapshot = useCallback(async (snapshotType) => {
    if (!selectedSprintId) return;

    setCaptureType(snapshotType);
    setStatus("");
    try {
      const data = await apiRequest("/sn-metrics/snapshot", {
        method: "POST",
        body: JSON.stringify({ sprintId: selectedSprintId, snapshotType }),
      });
      setStatus(`${snapshotType === "start" ? "Start" : "End"} snapshot captured: ${data.snapshotKey}`);
      await loadSnapshots(selectedSprintId);
      await loadReport(selectedSprintId, {
        archiveDetails: snapshotType === "end",
        endSnapshotKey: snapshotType === "end" ? data.snapshotKey : "",
      });
    } catch (requestError) {
      setError(requestError.message || `Unable to capture ${snapshotType} snapshot.`);
    } finally {
      setCaptureType("");
    }
  }, [loadReport, loadSnapshots, selectedSprintId]);

  const handleDownloadReportJson = useCallback(() => {
    if (!report || !selectedSprintId) return;
    setExporting("json");
    try {
      downloadFile(
        `sprint-metrics-${selectedSprintId}.json`,
        `${JSON.stringify(report, null, 2)}\n`,
        "application/json;charset=utf-8"
      );
      setStatus("Downloaded report JSON.");
    } finally {
      setExporting("");
    }
  }, [report, selectedSprintId]);

  const handleDownloadSummaryCsv = useCallback(() => {
    if (!report || !selectedSprintId) return;
    setExporting("csv");
    try {
      downloadFile(
        `sprint-metrics-summary-${selectedSprintId}.csv`,
        `${buildSummaryCsv(report)}\n`,
        "text/csv;charset=utf-8"
      );
      setStatus("Downloaded summary CSV.");
    } finally {
      setExporting("");
    }
  }, [report, selectedSprintId]);

  const handleOpenRawReport = useCallback(() => {
    if (!report) return;
    setExporting("open");
    try {
      const blob = new Blob([`${JSON.stringify(report, null, 2)}\n`], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setStatus("Opened raw report in a new tab.");
    } finally {
      setExporting("");
    }
  }, [report]);

  const reportSummary = report?.summary || {};
  const unplannedCounts = report?.unplanned?.counts || {};
  const classifications = report?.classifications || {};
  const teamSummaries = report?.teamSummaries || [];
  const peopleSummary = useMemo(() => buildPeopleSummary(report), [report]);
  const filteredPeopleSummary = useMemo(() => {
    const search = assigneeSearch.trim().toLowerCase();
    if (!search) return peopleSummary;
    return peopleSummary.filter((row) => row.assignee.toLowerCase().includes(search));
  }, [peopleSummary, assigneeSearch]);
  const hasSnapshots = snapshots.length > 0;
  const showFirstRunGuide = !!selectedSprintId && !hasSnapshots && !loadingSnapshots && !loadingReport && !report && !error;

  return (
    <div className="cug-root metrics-root">
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark logo-mark-route"><ToolIcon toolId="sprint-metrics" size={19} /></div>
          <div>
            <div className="cug-title">Sprint Metrics</div>
            <div className="cug-subtitle">Capture snapshots, compare sprint scope changes, and review unplanned work</div>
          </div>
        </div>
      </div>

      <div className="cug-body sn-cug-body">
      <section className="metrics-toolbar-card">
        <div className="metrics-toolbar-grid">
          <label className="metrics-field">
            <span className="metrics-field-label">Sprint</span>
            <select
              className="metrics-select"
              title={selectedSprint?.name || ""}
              value={selectedSprintId}
              onChange={(event) => {
                setSelectedSprintId(event.target.value);
                setStatus("");
                setError("");
              }}
              disabled={loadingSprints || sprints.length === 0}
            >
              {sprints.map((sprint) => (
                <option key={sprint.sys_id} value={sprint.sys_id}>
                  {sprint.name}
                </option>
              ))}
            </select>
          </label>

          <div className="metrics-sprint-meta">
            <div>
              <span className="metrics-meta-label">State</span>
              <strong>{formatValue(selectedSprint?.state)}</strong>
            </div>
            <div>
              <span className="metrics-meta-label">Start</span>
              <strong>{formatDateLabel(selectedSprint?.start_date)}</strong>
            </div>
            <div>
              <span className="metrics-meta-label">End</span>
              <strong>{formatDateLabel(selectedSprint?.end_date)}</strong>
            </div>
          </div>

          <div className="metrics-action-row">
            <button
              className="metrics-primary-btn"
              onClick={() => handleCaptureSnapshot("start")}
              disabled={!selectedSprintId || captureType === "start"}
            >
              {captureType === "start" ? "Capturing Start..." : "Capture Start Snapshot"}
            </button>
            <button
              className="metrics-primary-btn metrics-primary-btn-alt"
              onClick={() => handleCaptureSnapshot("end")}
              disabled={!selectedSprintId || captureType === "end"}
            >
              {captureType === "end" ? "Capturing End..." : "Capture End Snapshot"}
            </button>
            <button
              className="metrics-secondary-btn"
              onClick={loadSprints}
              disabled={!selectedSprintId || loadingSprints}
            >
              {loadingSprints ? "Refreshing Sprints..." : "Refresh Sprints"}
            </button>
            <button
              className="metrics-secondary-btn"
              onClick={() => {
                if (!selectedSprintId) return;
                loadSnapshots(selectedSprintId);
                loadReport(selectedSprintId);
              }}
              disabled={!selectedSprintId || loadingSnapshots || loadingReport}
            >
              {loadingSnapshots || loadingReport ? "Refreshing..." : "Refresh Metrics"}
            </button>
          </div>
        </div>

        {status && <div className="metrics-status-banner">{status}</div>}
        {error && <div className="metrics-error-banner">{error}</div>}
      </section>

      {showFirstRunGuide && (
        <section className="metrics-onboarding-card">
          <div className="metrics-panel-header">
            <h2>First Run Guide</h2>
            <span className="metrics-report-key">No snapshots yet</span>
          </div>
          <div className="metrics-onboarding-steps">
            <div>
              <strong>1. Capture start snapshot</strong>
              <p>Take the opening scope snapshot when the sprint is planned.</p>
            </div>
            <div>
              <strong>2. Capture end snapshot</strong>
              <p>Take the closing snapshot when you want the report to compare against the start.</p>
            </div>
            <div>
              <strong>3. Refresh metrics</strong>
              <p>The report, classifications, and unplanned counts will populate automatically after both snapshots exist.</p>
            </div>
          </div>
        </section>
      )}

      <section className="metrics-grid metrics-grid-top">
        <article className="metrics-panel-card">
          <div className="metrics-panel-header">
            <h2>Snapshots</h2>
            <span className="metrics-count-badge">{snapshots.length}</span>
          </div>
          {loadingSnapshots ? (
            <div className="metrics-empty-state">Loading snapshots...</div>
          ) : snapshots.length === 0 ? (
            <div className="metrics-empty-state">No snapshots yet. Capture start and end snapshots for this sprint.</div>
          ) : (
            <div className="metrics-snapshot-list">
              {snapshots.map((snapshot) => (
                <SnapshotRow key={snapshot.key} snapshot={snapshot} />
              ))}
            </div>
          )}
        </article>

        <article className="metrics-panel-card">
          <div className="metrics-panel-header">
            <h2>Report Summary</h2>
            <div className="metrics-panel-actions">
              {report?.reportKey && <span className="metrics-report-key">Saved</span>}
              <button className="metrics-icon-btn" onClick={handleOpenRawReport} disabled={!report || exporting === "open"}>
                {exporting === "open" ? "Opening..." : "Open Raw Report"}
              </button>
              <button className="metrics-icon-btn" onClick={handleDownloadReportJson} disabled={!report || exporting === "json"}>
                {exporting === "json" ? "Exporting JSON..." : "Download JSON"}
              </button>
              <button className="metrics-icon-btn" onClick={handleDownloadSummaryCsv} disabled={!report || exporting === "csv"}>
                {exporting === "csv" ? "Exporting CSV..." : "Download CSV"}
              </button>
            </div>
          </div>
          {loadingReport ? (
            <div className="metrics-empty-state">Loading report...</div>
          ) : !report ? (
            <div className="metrics-empty-state">Capture start and end snapshots to generate the report summary and classification drilldowns.</div>
          ) : (
            <div className="metrics-summary-grid">
              <div className="metrics-stat-card">
                <span>Starting Scope</span>
                <strong>{formatValue(reportSummary.startingStories, "0")} stories</strong>
                <em>{formatValue(reportSummary.startingPoints, "0")} points</em>
              </div>
              <div className="metrics-stat-card">
                <span>Added</span>
                <strong>{formatValue(reportSummary.addedStories, "0")} stories</strong>
                <em>{formatValue(reportSummary.addedPoints, "0")} points</em>
              </div>
              <div className="metrics-stat-card">
                <span>Moved Out</span>
                <strong>{formatValue(reportSummary.canceledOrMovedOutStories, "0")} stories</strong>
                <em>{formatValue(reportSummary.canceledOrMovedOutPoints, "0")} points</em>
              </div>
              <div className="metrics-stat-card">
                <span>Carryover</span>
                <strong>{formatValue(reportSummary.carryoverStories, "0")} stories</strong>
                <em>{formatValue(reportSummary.carryoverPoints, "0")} points</em>
              </div>
              <div className="metrics-stat-card">
                <span>Completed by End</span>
                <strong>{formatValue(reportSummary.endingStories, "0")} stories</strong>
                <em>{formatValue(reportSummary.endingPoints, "0")} points</em>
              </div>
              <div className="metrics-stat-card">
                <span>Difference</span>
                <strong>{formatValue(reportSummary.storyDifference, "0")} stories</strong>
                <em>{formatValue(reportSummary.pointDifference, "0")} points</em>
              </div>
            </div>
          )}
        </article>
      </section>

      <section className="metrics-grid metrics-grid-middle">
        <article className="metrics-panel-card metrics-unplanned-panel">
          <div className="metrics-panel-header">
            <h2>Unplanned Work</h2>
            <span className="metrics-count-badge">{formatValue(unplannedCounts.totalUnplanned, "0")}</span>
          </div>
          {report?.unplanned?.error ? (
            <div className="metrics-error-inline">{report.unplanned.error}</div>
          ) : !report?.unplanned ? (
            <div className="metrics-empty-state">Unplanned work metrics will appear with the sprint report.</div>
          ) : (
            <div className="metrics-summary-grid metrics-summary-grid-compact">
              <div className="metrics-stat-card metrics-stat-card-compact"><span>Incidents</span><strong>{formatValue(unplannedCounts.incidents, "0")}</strong></div>
              <div className="metrics-stat-card metrics-stat-card-compact"><span>HRC</span><strong>{formatValue(unplannedCounts.hrc, "0")}</strong></div>
              <div className="metrics-stat-card metrics-stat-card-compact"><span>Tasks</span><strong>{formatValue(unplannedCounts.tasks, "0")}</strong></div>
              <div className="metrics-stat-card metrics-stat-card-compact"><span>KFTs</span><strong>{formatValue(unplannedCounts.kfts, "0")}</strong></div>
              <div className="metrics-stat-card metrics-stat-card-compact"><span>Wrong Type</span><strong>{formatValue(unplannedCounts.incidentsWrongType, "0")}</strong></div>
              <div className="metrics-stat-card metrics-stat-card-compact"><span>Missing Affected CI</span><strong>{formatValue(unplannedCounts.incOrTaskMissingAffectedCi, "0")}</strong></div>
            </div>
          )}
        </article>
      </section>

      <section className="metrics-panel-card">
        <div className="metrics-panel-header">
          <h2>Per-Person Summary</h2>
          <div className="metrics-panel-actions">
            <span className="metrics-count-badge">{filteredPeopleSummary.length}</span>
          </div>
        </div>
        {report ? (
          <>
            <div className="metrics-people-toolbar">
              <input
                className="metrics-select metrics-search-input"
                placeholder="Filter by assignee"
                value={assigneeSearch}
                onChange={(event) => setAssigneeSearch(event.target.value)}
              />
            </div>
            {filteredPeopleSummary.length === 0 ? (
              <div className="metrics-empty-state">No assignees match your filter.</div>
            ) : (
              <div className="metrics-table-wrap">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>Assignee</th>
                      <th>Total Stories</th>
                      <th>Total Points</th>
                      <th>Added</th>
                      <th>Carryover</th>
                      <th>Stayed In Sprint</th>
                      <th>Moved Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPeopleSummary.map((row) => (
                      <tr key={row.assignee}>
                        <td>{row.assignee}</td>
                        <td>{row.totalStories}</td>
                        <td>{formatValue(row.totalPoints, "0")}</td>
                        <td>{row.added}</td>
                        <td>{row.carryover}</td>
                        <td>{row.stayedInSprint}</td>
                        <td>{row.movedOut}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="metrics-empty-state">Per-person breakdown will appear when a report is available.</div>
        )}
      </section>

      <ClassificationTable
        title="Stayed In Sprint"
        rows={classifications.stayedInSprint || []}
        fullWidth
        scrollable
      />

      <div className="metrics-classification-grid">
        <ClassificationTable title="Added Stories" rows={classifications.added || []} />
        <ClassificationTable title="Carryover Stories" rows={classifications.carryover || []} />
        <ClassificationTable title="Moved To Backlog" rows={classifications.movedToBacklog || []} />
        <ClassificationTable title="Moved To Other Sprint" rows={classifications.movedToOtherSprint || []} />
      </div>

      <section className="metrics-panel-card metrics-team-summary-panel">
        <div className="metrics-panel-header">
          <h2>Team Summaries</h2>
          <div className="metrics-panel-actions">
            <span className="metrics-count-badge">{teamSummaries.length}</span>
            <button
              className="metrics-secondary-btn"
              onClick={() => loadReport(selectedSprintId, {
                archiveDetails: true,
                endSnapshotKey: report.snapshotKeys.end,
              })}
              disabled={!report?.snapshotKeys?.end || loadingReport || !!captureType || teamSummaries.length > 0}
            >
              {generatingSummaries ? "Generating Summaries..." : "Generate Team Summaries"}
            </button>
          </div>
        </div>
        {generatingSummaries ? (
          <div className="metrics-empty-state">Generating summaries from descriptions and work notes for this end snapshot...</div>
        ) : report?.teamSummaryError ? (
          <div className="metrics-error-inline">Unable to generate team summaries: {report.teamSummaryError}</div>
        ) : teamSummaries.length > 0 ? (
          <div className="metrics-team-summary-scroll">
            {teamSummaries.map((item) => (
              <article className="metrics-team-summary" key={item.assignee}>
                <h3>{item.assignee}</h3>
                <p>{item.summary}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="metrics-empty-state">No saved summaries for this end snapshot. Select Generate Team Summaries to generate or retry without capturing another snapshot.</div>
        )}
      </section>
      </div>
    </div>
  );
}